import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { discoveryExtraRuleCount } from '@/lib/screenerDiscovery'
import { discoverySortForPeriod } from '@/lib/screenerDiscoveryDefaults'
import type { ScreenerDiscoveryFilters } from '@/types'
import { DefaultsFooter, FilterChip, type FilterDefaults } from './FilterSelectionControls'

interface DiscoveryFilterControlsProps {
  filters: ScreenerDiscoveryFilters
  availableTags: string[]
  defaults: FilterDefaults
  onChange: (filters: ScreenerDiscoveryFilters) => void
  onDone: () => void
  resultCount: number
}

function parseThreshold(value: string): number {
  const normalized = Number(value.replace(/,/g, '').trim())
  return Number.isFinite(normalized) && normalized > 0 ? normalized : 0
}

const choiceClass = (active: boolean) => cn(
  'min-w-0 flex-1 rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-400',
  active ? 'bg-teal-400/15 text-teal-300 shadow-sm' : 'text-slate-500 hover:text-slate-200',
)

export function DiscoveryFilterControls({ filters, availableTags, defaults, onChange, onDone, resultCount }: DiscoveryFilterControlsProps) {
  const [moreOpen, setMoreOpen] = useState(false)
  const perExchange = filters.filterScope === 'exchange'
  const period = filters.changePeriod ?? '24h'
  const extraRuleCount = discoveryExtraRuleCount(filters)
  const maxMatches = Math.max(1, filters.exchanges.length)
  const changeScope = (filterScope: ScreenerDiscoveryFilters['filterScope']) => {
    onChange({
      ...filters,
      filterScope,
      sortBy: filterScope === 'exchange' ? `matching_change_pct_${period}` : `oi_change_pct_${period}`,
      sortDirection: 'desc',
    })
  }
  const toggleTag = (tag: string, excluded: boolean) => {
    const key = excluded ? 'excludeTags' : 'includeTags'
    const other = excluded ? 'includeTags' : 'excludeTags'
    onChange({
      ...filters,
      [key]: filters[key].includes(tag) ? filters[key].filter((value) => value !== tag) : [...filters[key], tag],
      [other]: filters[other].filter((value) => value !== tag),
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold text-slate-100">Discovery filters</h3>
        <button
          type="button"
          onClick={() => onChange({ ...filters, changeDirection: 'either', minOiUsd: 0, minAbsChangePct: 0, minAbsChangeUsd: 0, minMatchingExchanges: 1, includeTags: [], excludeTags: [] })}
          className="text-[11px] text-slate-500 hover:text-slate-200"
        >Reset</button>
      </div>

      <div className="flex gap-1 rounded-lg border border-slate-800 bg-slate-950/80 p-1" role="group" aria-label="OI filter scope">
        <button type="button" aria-pressed={!perExchange} className={choiceClass(!perExchange)} onClick={() => changeScope('combined')}>Combined</button>
        <button type="button" aria-pressed={perExchange} className={choiceClass(perExchange)} onClick={() => changeScope('exchange')}>Per exchange</button>
      </div>
      <p className="text-[11px] leading-relaxed text-slate-500">
        {perExchange
          ? `At least ${Math.min(filters.minMatchingExchanges, maxMatches)} selected exchange${Math.min(filters.minMatchingExchanges, maxMatches) === 1 ? '' : 's'} must meet all your rules. Each exchange is checked separately.`
          : 'Rules apply to the combined OI across your selected exchanges.'}
      </p>

      <div>
        <div className="mb-1.5 text-[11px] text-slate-300">OI change period</div>
        <div className="flex gap-1 rounded-lg border border-slate-800 bg-slate-950/80 p-1" role="group" aria-label="OI change period">
          {(['15m', '1h', '24h'] as const).map((changePeriod) => (
            <button key={changePeriod} type="button" aria-pressed={period === changePeriod} className={choiceClass(period === changePeriod)}
              onClick={() => onChange({ ...filters, changePeriod, sortBy: discoverySortForPeriod(filters.sortBy, changePeriod) })}>{changePeriod}</button>
          ))}
        </div>
        <p className="mt-1.5 text-[10px] leading-relaxed text-slate-500">When the full period is unavailable, change uses the earliest available datapoint on each exchange.</p>
      </div>

      <label className="grid grid-cols-[minmax(0,1fr)_112px] items-center gap-3 text-[11px] text-slate-300">
        Minimum {period} change (%)
        <Input type="number" min="0" step="0.1" value={filters.minAbsChangePct || ''} placeholder="0"
          onChange={(event) => onChange({ ...filters, minAbsChangePct: parseThreshold(event.target.value) })}
          className="h-8 border-slate-800 bg-slate-950/70 text-right text-xs" />
      </label>
      <div className="flex gap-1 rounded-lg border border-slate-800 bg-slate-950/80 p-1" role="group" aria-label="OI change direction">
        {([{ value: 'either', label: 'Either' }, { value: 'increase', label: 'Increase' }, { value: 'decrease', label: 'Decrease' }] as const).map(({ value, label }) => (
          <button key={value} type="button" aria-pressed={filters.changeDirection === value} className={choiceClass(filters.changeDirection === value)} onClick={() => onChange({ ...filters, changeDirection: value })}>{label}</button>
        ))}
      </div>

      <div className="border-t border-slate-800/80 pt-2">
        <button type="button" aria-expanded={moreOpen} onClick={() => setMoreOpen(!moreOpen)} className="flex w-full items-center justify-between py-1 text-[11px] text-slate-400 hover:text-slate-200">
          <span>More filters{extraRuleCount > 0 && <span className="ml-1.5 rounded bg-teal-400/10 px-1.5 py-0.5 text-teal-300">{extraRuleCount} active</span>}</span>
          {moreOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {moreOpen && (
          <div className="space-y-3 pt-3">
            <label className="grid grid-cols-[minmax(0,1fr)_112px] items-center gap-3 text-[11px] text-slate-400">
              <span>Minimum current OI ($)<span className="mt-0.5 block text-[10px] text-slate-600">{perExchange ? 'On each matching exchange' : 'Across selected exchanges'}</span></span>
              <Input type="number" min="0" value={filters.minOiUsd || ''} placeholder="0"
                onChange={(event) => onChange({ ...filters, minOiUsd: parseThreshold(event.target.value) })}
                className="h-8 border-slate-800 bg-slate-950/70 text-right text-xs" />
            </label>
            <label className="grid grid-cols-[minmax(0,1fr)_112px] items-center gap-3 text-[11px] text-slate-400">
              <span>Minimum {period} change ($)<span className="mt-0.5 block text-[10px] text-slate-600">{perExchange ? 'On the same exchange' : 'Combined change'}</span></span>
              <Input type="number" min="0" value={filters.minAbsChangeUsd || ''} placeholder="0"
                onChange={(event) => onChange({ ...filters, minAbsChangeUsd: parseThreshold(event.target.value) })}
                className="h-8 border-slate-800 bg-slate-950/70 text-right text-xs" />
            </label>
            {perExchange && (
              <label className="grid grid-cols-[minmax(0,1fr)_112px] items-center gap-3 text-[11px] text-slate-400">
                Matching exchanges
                <select aria-label="Minimum matching exchanges" value={Math.min(filters.minMatchingExchanges, maxMatches)} disabled={filters.exchanges.length === 0}
                  onChange={(event) => onChange({ ...filters, minMatchingExchanges: Number(event.target.value) })}
                  className="h-8 rounded-md border border-slate-800 bg-slate-950 px-2 text-[11px] text-slate-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-400 disabled:opacity-50">
                  {Array.from({ length: maxMatches }, (_, index) => index + 1).map((count) => <option key={count} value={count}>At least {count}</option>)}
                </select>
              </label>
            )}
            <p className="text-[10px] leading-relaxed text-slate-600">Change thresholds use magnitude. Choose Increase or Decrease to limit direction.</p>
            {([{ excluded: false, title: 'Show tags', helper: 'Symbols must contain every selected tag.' }, { excluded: true, title: 'Ignore tags', helper: 'Symbols containing any selected tag are hidden.' }] as const).map(({ excluded, title, helper }) => (
              <div key={title} className="border-t border-slate-800/70 pt-3">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{title}</div>
                <p className="mb-2 text-[10px] text-slate-600">{helper}</p>
                <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto" role="group" aria-label={title}>
                  {availableTags.map((tag) => <FilterChip key={tag} value={tag} selected={(excluded ? filters.excludeTags : filters.includeTags).includes(tag)} disabled={false} className="text-[10px]" onClick={() => toggleTag(tag, excluded)} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <DefaultsFooter label="discovery" defaults={defaults} className="border-slate-800/80" />
      <button type="button" onClick={onDone} className="w-full rounded-md bg-teal-400/15 py-2 text-[11px] font-semibold text-teal-300 hover:bg-teal-400/25">Show {resultCount} symbol{resultCount === 1 ? '' : 's'}</button>
    </div>
  )
}
